<template>
  <header :class="[isSticky ? 'sticky' : 'fixed', 'left-0 top-0 z-50 w-full py-4']">
    <div class="display-block container relative z-30 w-full items-center">
      <div class="flex justify-between gap-x-4">
        <RouterLink v-if="hasBackButton" :to="{ name: 'FanzoneIndex' }">
          <img src="/back3.svg" alt="back" />
        </RouterLink>
        <div v-else></div>
        <StarButton
          :tabindex="0"
          class="transition-transform duration-300"
          :class="isNavOpen && 'rotate-[360deg]'"
          @click="toggleNav"
          @keydown.enter="toggleNav"
        />
      </div>
    </div>
    <div
      class="fixed left-0 top-0 h-dvh w-screen bg-[url('../feed-bg.png')] bg-cover bg-fixed bg-no-repeat transition-opacity"
      :class="overlayClasses"
    >
      <div class="container flex h-full w-full flex-col items-end justify-between px-4 pb-8 pt-20">
        <div class="grid w-max justify-end gap-4 text-white">
          <template v-for="(item, index) in navItems" :key="item.id">
            <router-link
              v-if="!item.isExternal"
              :to="item.href"
              class="text-right text-3xl hover:underline"
              :class="[
                isNavOpen ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0',
                'transition-all duration-300 ease-out',
              ]"
              :style="{ 'transition-delay': `${index * 0.1}s` }"
              active-class="text-primary"
              @click="item?.closeOnClick && closeNav()"
            >
              {{ item.label }}
            </router-link>
            <a
              v-else
              :href="item.href"
              target="_blank"
              rel="noopener noreferrer"
              class="text-right text-3xl hover:underline"
              :class="[
                isNavOpen ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0',
                'transition-all duration-300 ease-out',
              ]"
              :style="{ 'transition-delay': `${index * 0.1}s` }"
              @click="trackExternalLink(item.href)"
            >
              {{ item.label }}
            </a>
          </template>
          <span
            :tabindex="0"
            class="cursor-pointer text-right text-3xl text-white hover:underline"
            :class="[
              isNavOpen ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0',
              'transition-all duration-300 ease-out',
            ]"
            :style="{ 'transition-delay': `${2 * 0.1}s` }"
            :label="t('common.share')"
            @click="startShare"
            @keydown.enter="startShare"
          >
            {{ t('common.share') }}
          </span>
          <a
            href="https://shoplanadelrey.com/?utm_source=SparkleJumpRopeQueenWebsite"
            target="_blank"
            rel="noopener noreferrer"
            class="text-right text-3xl hover:underline"
            :class="[
              isNavOpen ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0',
              'transition-all duration-300 ease-out',
            ]"
            :style="{ 'transition-delay': `${3 * 0.1}s` }"
            @click="
              trackExternalLink(
                'https://shoplanadelrey.com/?utm_source=SparkleJumpRopeQueenWebsite',
              )
            "
          >
            {{ t('common.officialStore') }}
          </a>
          <router-link
            v-if="showAccountMenu"
            :to="{ name: 'Profile' }"
            class="text-right text-3xl hover:underline"
            :class="[
              isNavOpen ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0',
              'transition-all duration-300 ease-out',
            ]"
            :style="{ 'transition-delay': `${4 * 0.1}s` }"
            active-class="text-primary"
            @click="closeNav()"
          >
            {{ t('common.account') }}
          </router-link>
        </div>
      </div>
      <div class="absolute left-0 top-0 -z-10 h-dvh w-screen" @click="closeNav"></div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useScrollLock } from '@vueuse/core';
import { useRoute } from 'vue-router';
import { useShare } from '@vueuse/core';

// Components
import Button from '@ui/button/Button.vue';
import StarButton from '@modules/Buttons/StarButton.vue';
import { useTelemetryFetcher } from '@api/tracking.api';

defineProps({
  hasBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const { share } = useShare();
const route = useRoute();
const isLocked = useScrollLock(document);

const navItems = computed(() => [
  {
    id: 1,
    label: route.name !== 'FanzoneIndex' ? 'Sparkle Jump Rope Queen' : t('common.tv'),
    href: route.name !== 'FanzoneIndex' ? '/fanzone' : '/',
    isExternal: false,
    closeOnClick: true,
  },
]);
const isNavOpen = ref(false);

const showAccountMenu = computed(() => {
  return import.meta.env.VITE_FF_ACCOUNT_MENU === 'true';
});

const isSticky = computed(() => {
  switch (route.name) {
    case 'FanzoneIndex':
    case 'FanzonePost':
      return true;
    default:
      return false;
  }
});
const overlayClasses = computed(() =>
  isNavOpen.value
    ? 'bg-black/70 pointer-events-auto backdrop-blur-lg opacity-100'
    : 'pointer-events-none opacity-0 -translate-y-full',
);

const toggleNav = () => {
  isNavOpen.value = !isNavOpen.value;
  isLocked.value = !isLocked.value;
};
const closeNav = () => {
  isNavOpen.value = false;
  isLocked.value = false;
};
const startShare = async () => {
  try {
    await share({
      title: 'Sparkle Jump Rope Queen',
      text: 'Lana del Rey',
      url: import.meta.env.VITE_HOME_URL,
    });
    useTelemetryFetcher().execute({
      metric: 'share',
      resource: window.location.href,
    });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      // Share was cancelled by user, we can handle this case if needed
      console.log('Share was cancelled');
    } else {
      // Handle other potential errors
      console.error('Error sharing:', error);
    }
  }
};

const trackExternalLink = (href: string) => {
  useTelemetryFetcher().execute({
    metric: 'click-link',
    resource: href,
  });
};

watch(
  () => route.fullPath,
  () => {
    if (isNavOpen.value) {
      closeNav();
    }
  },
);
</script>

<style scoped>
.text-oat {
  color: hsla(var(--oat), 1);
}
.text-cream {
  color: #c4c4c4;
}
</style>
